import clsx from 'clsx';
import type React from 'react';
import { useState, useEffect, useRef } from 'react';

const widths = {
  xs: 'tw:max-w-xs',
  sm: 'tw:max-w-sm',
  md: 'tw:max-w-md',
  lg: 'tw:max-w-lg',
  xl: 'tw:max-w-xl',
  '2xl': 'tw:max-w-2xl',
  '3xl': 'tw:max-w-3xl',
  '4xl': 'tw:max-w-4xl',
  '5xl': 'tw:max-w-5xl',
  full: 'tw:max-w-full',
};

type DrawerMode = 'modal' | 'side-by-side';

interface DrawerProps {
  width?: keyof typeof widths;
  className?: string;
  children: React.ReactNode;
  mode?: DrawerMode;
  open: boolean;
  onClose: () => void;
}

// Hook to detect if we're on desktop (lg breakpoint and above)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

export function Drawer({
  width = 'lg',
  className,
  children,
  mode = 'side-by-side',
  open,
  onClose,
}: DrawerProps) {
  const isDesktop = useIsDesktop();
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Side-by-side mode: render modal on mobile, side-by-side on desktop
  if (mode === 'side-by-side') {
    return (
      <>
        {/* Modal mode on mobile/tablet */}
        {!isDesktop && open && (
          <div
            role="dialog"
            aria-modal="true"
            className="tw:fixed tw:inset-0 tw:z-50 tw:overflow-hidden"
          >
            {/* Backdrop */}
            <div
              className="tw:fixed tw:inset-0 tw:bg-zinc-950/25 tw:transition-opacity tw:duration-300 tw:dark:bg-zinc-950/50"
              onClick={onClose}
              aria-hidden="true"
            />

            {/* Panel */}
            <div className="tw:fixed tw:inset-y-0 tw:right-0 tw:flex tw:max-w-full tw:pl-10">
              <div
                ref={panelRef}
                className={clsx(
                  className,
                  widths[width],
                  'tw:w-screen tw:h-full tw:flex tw:flex-col tw:bg-white tw:shadow-xl tw:ring-1 tw:ring-zinc-950/10 tw:dark:bg-zinc-900 tw:dark:ring-white/10',
                  'tw:transition-transform tw:duration-300 tw:ease-out'
                )}
              >
                {children}
              </div>
            </div>
          </div>
        )}

        {/* Side-by-side mode on desktop */}
        {isDesktop && open && (
          <aside
            className={clsx(
              className,
              'tw:flex tw:flex-col tw:w-2/5 tw:bg-white tw:border-l tw:border-zinc-950/20 tw:shadow-2xl tw:dark:bg-zinc-900 tw:dark:border-white/10 tw:h-full tw:overflow-hidden'
            )}
          >
            {children}
          </aside>
        )}
      </>
    );
  }

  // Modal mode (always modal)
  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" className="tw:fixed tw:inset-0 tw:z-50 tw:overflow-hidden">
      {/* Backdrop */}
      <div
        className="tw:fixed tw:inset-0 tw:bg-zinc-950/25 tw:transition-opacity tw:duration-300 tw:dark:bg-zinc-950/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="tw:fixed tw:inset-y-0 tw:right-0 tw:flex tw:max-w-full tw:pl-10">
        <div
          ref={panelRef}
          className={clsx(
            className,
            widths[width],
            'tw:w-screen tw:h-full tw:flex tw:flex-col tw:bg-white tw:shadow-xl tw:ring-1 tw:ring-zinc-950/10 tw:dark:bg-zinc-900 tw:dark:ring-white/10',
            'tw:transition-transform tw:duration-300 tw:ease-out'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

type DrawerHeaderProps = {
  className?: string;
  children: React.ReactNode;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerHeader({ className, children, ...props }: DrawerHeaderProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:border-b tw:border-zinc-950/5 tw:dark:border-white/5 tw:text-zinc-900 tw:dark:text-zinc-300'
      )}
    >
      {children}
    </div>
  );
}

type DrawerCloseButtonProps = {
  className?: string;
  'aria-label'?: string;
  onClick: () => void;
};

export function DrawerCloseButton({
  className,
  'aria-label': ariaLabel,
  onClick,
}: DrawerCloseButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel || 'Close drawer'}
      className={clsx(
        className,
        'tw:rounded-md tw:p-1.5 tw:text-zinc-400 tw:transition tw:hover:bg-zinc-100 tw:hover:text-zinc-500 tw:dark:hover:bg-zinc-800 tw:dark:hover:text-zinc-300'
      )}
    >
      <svg
        className="tw:h-6 tw:w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

type DrawerTitleProps = {
  className?: string;
  children: React.ReactNode;
} & React.ComponentPropsWithoutRef<'h2'>;

export function DrawerTitle({ className, children, ...props }: DrawerTitleProps) {
  return (
    <h2 {...props} className={className}>
      {children}
    </h2>
  );
}

type DrawerBodyProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerBody({ className, ...props }: DrawerBodyProps) {
  return (
    <div
      {...props}
      className={clsx(className, 'tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4')}
    />
  );
}

type DrawerFooterProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DrawerFooter({ className, ...props }: DrawerFooterProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:items-center tw:justify-end tw:gap-3 tw:px-6 tw:py-4 tw:border-t tw:border-zinc-950/5 tw:dark:border-white/5'
      )}
    />
  );
}
